import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { IAccordionData } from '../../../components/Accordion/Accordion';
import { EmptyCheckboxIcon } from '../../../components/Icons/EmptyCheckboxIcon';
import { RadioOnIcon } from '../../../components/Icons/RadioOnIcon';
import { getNodesFromConnection } from '../../../helpers/graphql';
import {
  GetFishingWaterMetadata,
  GetFishingWaterMetadata_fishingWaterMetadata_edges_node,
} from '../../../interfaces/graphql';
import { SuggestedWaterFormState } from '../types/SuggestedWaterForm';

interface TypeAccordionBlockData {
  title: string;
  entries: GetFishingWaterMetadata_fishingWaterMetadata_edges_node[];
  id: string;
}

type MetadataGroup = Record<
  string,
  { entries: GetFishingWaterMetadata_fishingWaterMetadata_edges_node[]; groupName: string }
>;

/**
 * Retrieves all fishing water metadata entries and organizes them by group into accordion blocks.
 *
 * @param allMetadata - The fishing water metadata.
 * @param register - The registration function for form inputs.
 * @param currentWaterMetadata - The current water metadata.
 * @returns An array of accordion data.
 */
export const getFishingWaterMetadataEntries = ({
  allMetadata,
  currentWaterMetadata,
  watch,
  setValue,
  register,
}: {
  allMetadata: GetFishingWaterMetadata;
  currentWaterMetadata: { groupId: string; id: string }[];
  watch: UseFormWatch<SuggestedWaterFormState>;
  setValue: UseFormSetValue<SuggestedWaterFormState>;
  register: UseFormRegister<SuggestedWaterFormState>;
}): IAccordionData[] => {
  const groupedMetadata: MetadataGroup = getNodesFromConnection(
    allMetadata.fishingWaterMetadata,
  ).reduce(
    (groups: MetadataGroup, item) => ({
      ...groups,
      [item.group.id]: {
        // Type is wrong, the check is needed.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        entries: [...(groups[item.group.id]?.entries || []), item],
        groupName: item.group.name,
      },
    }),
    {},
  );

  const getAccordionBlockData = ({
    title,
    entries,
    id,
  }: TypeAccordionBlockData): IAccordionData => {
    const selectedValue = watch(`fishingWaterMetadata.${id}`);

    const handleIconClick = (value: string): void => {
      setValue(`fishingWaterMetadata.${id}`, value);
    };

    return {
      id,
      title,
      body: (
        <div className="flex flex-col">
          {entries.map((item: GetFishingWaterMetadata_fishingWaterMetadata_edges_node) => (
            <div
              key={item.id}
              className="mb-4 flex cursor-pointer"
              role="radio"
              tabIndex={0}
              onKeyDown={() => {
                handleIconClick(item.id);
              }}
              onClick={() => {
                handleIconClick(item.id);
              }}
              aria-checked={selectedValue === item.id}
            >
              <div>
                {selectedValue === item.id ||
                (!selectedValue &&
                  currentWaterMetadata.find(entry => entry.groupId === id)?.id === item.id) ? (
                  <RadioOnIcon />
                ) : (
                  <EmptyCheckboxIcon />
                )}
              </div>
              <label className="ml-1.5 cursor-pointer">{item.name}</label>
              <input
                type="radio"
                value={item.id}
                hidden
                checked={selectedValue === item.id}
                {...register(`fishingWaterMetadata.${id}` as const)}
              />
            </div>
          ))}
        </div>
      ),
    };
  };

  return groupedMetadata
    ? Object.keys(groupedMetadata).map(id =>
        getAccordionBlockData({
          title: groupedMetadata[id].groupName,
          entries: groupedMetadata[id].entries,
          id,
        }),
      )
    : [];
};
